import type { NotificacaoListItem } from '@/features/extrajudicial/types';
import { formatDate, formatTime } from '@/shared/formatters';

type CsvCell = string | number | boolean | null | undefined;

const escapeCsvCell = (value: CsvCell): string => {
  if (value === null || value === undefined) return '""';

  const normalized = String(value);
  const escaped = normalized.replaceAll('"', '""');
  return `"${escaped}"`;
};

const formatPtBrDateTime = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const datePart = formatDate(value);
  const timePart = formatTime(value);
  if (!datePart || !timePart) return value;
  return `${datePart} às ${timePart}`;
};

type Envio = {
  canal: string;
  statusEmail?: string | null;
  statusWhatsApp?: string | null;
  dataEnvio: string | null;
};

export const buildNotificationCsv = (items: NotificacaoListItem[]): string => {
  const maxEnviosDigitais = Math.max(...items.map((item) => item.enviosDigitais?.length ?? 0), 1);

  const hasEnvioFisico = items.some((item) => item.envioFisico);

  const headers = [
    'ID',
    'Pessoa notificada',
    'Status',
    ...Array.from({ length: maxEnviosDigitais }, (_, i) =>
      maxEnviosDigitais > 1 ? `Situação de entrega ${i + 1}` : 'Situação de entrega',
    ),
    ...(hasEnvioFisico ? ['Situação de entrega (Físico)'] : []),
    'Tem manifestação',
    'Criado em',
    'Último evento',
  ];

  const rows = items.map((item) => {
    const envios: Envio[] = item.enviosDigitais ?? [];

    const situacoesDigitais = envios.map((envio) => {
      const status = envio.canal === 'WHATSAPP' ? envio.statusWhatsApp : envio.statusEmail;

      const canalLabel = envio.canal === 'WHATSAPP' ? 'Whatsapp' : 'Email';

      return `${canalLabel} - ${status ?? ''}`;
    });

    while (situacoesDigitais.length < maxEnviosDigitais) {
      situacoesDigitais.push('');
    }

    let situacaoFisico: string[] = [];

    if (hasEnvioFisico) {
      if (item.envioFisico) {
        situacaoFisico = [`Físico - ${item.envioFisico.statusCorreios ?? ''}`];
      } else {
        situacaoFisico = [''];
      }
    }

    const values: CsvCell[] = [
      item.numeroNotificacao,
      item.notificado.nome,
      item.statusLabel,
      ...situacoesDigitais,
      ...situacaoFisico,
      item.temManifestacao ? 'Sim' : 'Não',
      item.dataCriacao ? formatPtBrDateTime(item.dataCriacao) : '',
      item.lastEventAt ? formatPtBrDateTime(item.lastEventAt) : '',
    ];

    return values.map(escapeCsvCell).join(',');
  });

  return [headers.map(escapeCsvCell).join(','), ...rows].join('\n');
};
